import { useState, type FormEvent } from "react";
import { Upload } from "lucide-react";
import { submitTestimonial, type CreditStyle } from "../utils/testimonialSubmission";
import { trackEvent } from "../utils/analytics";
import SuccessCheckmark from "./SuccessCheckmark";

type Status = "idle" | "loading" | "success";

const CREDIT_OPTIONS: { value: CreditStyle; label: string }[] = [
  { value: "full-name", label: "Full name" },
  { value: "initials", label: "Initials only" },
  { value: "anonymous", label: "Anonymous" },
];

export default function TestimonialSubmissionForm() {
  const [quote, setQuote] = useState("");
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [creditStyle, setCreditStyle] = useState<CreditStyle>("full-name");
  const [consentGiven, setConsentGiven] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>("idle");

  if (status === "success") {
    return (
      <div role="status" aria-live="polite" className="rounded-2xl bg-teal-400/10 p-8 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-teal-400/20 text-teal-600">
          <SuccessCheckmark className="h-6 w-6" />
        </div>
        <h3 className="mt-4 font-display text-lg font-semibold text-ocean-950">Thanks for sharing your story</h3>
        <p className="mt-2 text-sm text-ocean-800/70">
          Your submission is now in our review queue and won&apos;t be published until it&apos;s approved. Want
          it taken down later? Contact us and we&apos;ll remove it within 72 hours.
        </p>
      </div>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!consentGiven) return;
    setStatus("loading");
    try {
      await submitTestimonial({
        quote,
        creditStyle,
        name: creditStyle === "anonymous" ? undefined : name,
        city: city || undefined,
        consentGiven,
      });
      trackEvent("testimonial_submit", { creditStyle });
      setStatus("success");
    } catch {
      setStatus("idle");
    }
  };

  const creditDisplayName =
    creditStyle === "full-name" ? name || "your full name" : creditStyle === "initials" ? "your initials" : '"Anonymous"';

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-5 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-ocean-900/5 sm:p-8"
    >
      <label className="block text-sm">
        <span className="font-medium text-ocean-900">Your story</span>
        <textarea
          required
          rows={4}
          value={quote}
          onChange={(e) => setQuote(e.target.value)}
          className="mt-1.5 w-full resize-none rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400"
        />
      </label>

      <div className="grid gap-5 sm:grid-cols-2">
        {creditStyle !== "anonymous" && (
          <label className="block text-sm">
            <span className="font-medium text-ocean-900">Your name</span>
            <input
              required
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400"
            />
          </label>
        )}
        <label className="block text-sm">
          <span className="font-medium text-ocean-900">City</span>
          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400"
          />
        </label>
      </div>

      <fieldset className="text-sm">
        <legend className="font-medium text-ocean-900">How should we credit you?</legend>
        <div className="mt-2 flex flex-wrap gap-4">
          {CREDIT_OPTIONS.map((opt) => (
            <label key={opt.value} className="flex items-center gap-2">
              <input
                type="radio"
                name="creditStyle"
                checked={creditStyle === opt.value}
                onChange={() => setCreditStyle(opt.value)}
                className="accent-teal-500"
              />
              {opt.label}
            </label>
          ))}
        </div>
      </fieldset>

      <label className="block text-sm">
        <span className="font-medium text-ocean-900">Photo or video (optional)</span>
        <div className="mt-1.5 flex items-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-ocean-900/5 px-4 py-2 text-xs font-semibold text-ocean-800 hover:bg-ocean-900/10">
            <Upload className="h-3.5 w-3.5" />
            Choose file
            <input
              type="file"
              accept="image/*,video/*"
              className="hidden"
              onChange={(e) => setFileName(e.target.files?.[0]?.name ?? null)}
            />
          </label>
          {fileName && <span className="truncate text-xs text-ocean-800/60">{fileName}</span>}
        </div>
      </label>

      <label className="flex items-start gap-3 text-xs leading-relaxed text-ocean-800/70">
        <input
          required
          type="checkbox"
          checked={consentGiven}
          onChange={(e) => setConsentGiven(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 accent-teal-500"
        />
        <span>
          I consent to ZenFresh publishing my quote above, credited as {creditDisplayName}
          {fileName ? ", along with the photo/video I've attached" : ""}, on the ZenFresh website and marketing
          materials, until I request its removal.
        </span>
      </label>

      <button
        type="submit"
        disabled={status === "loading" || !consentGiven}
        className="flex w-full items-center justify-center gap-1.5 rounded-full bg-teal-400 py-3 text-sm font-semibold text-ocean-950 transition-colors hover:bg-teal-300 disabled:opacity-50"
      >
        {status === "loading" ? "Submitting…" : "Submit My Story"}
      </button>
    </form>
  );
}
